import { ArrowLeft, Thermometer, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import welcomeBg from "../../assets/homebg.png";
import fitnessService, { FitnessData } from "../../services/FitnessService";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

const TemperatureDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");
  const [fitnessData, setFitnessData] = useState<FitnessData | null>(
    location.state?.fitnessData || null
  );
  const [loading, setLoading] = useState(!location.state?.fitnessData);

  const [dateRange, setDateRange] = useState({ start_date: "", end_date: "" });

  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) return "";
    const s = new Date(dateRange.start_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const e = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${s} - ${e}`;
  };



  // Get selected date from navigation state or use today
  const selectedDate =
    location.state?.selectedDate || new Date().toISOString().split("T")[0];

  useEffect(() => {
    // Only fetch if data not passed from previous page
    if (!location.state?.fitnessData) {
      const fetchTemperatureData = async () => {
        try {
          setLoading(true);
          const result = await fitnessService.getDailyFitnessData(selectedDate);
          const isSuccess =
            typeof result.success === "boolean"
              ? result.success
              : result.success?.code === "Retrieved";
          if (result && isSuccess && result.data) {
            setFitnessData(result.data);
          }
        } catch (error) {
          // Handle error silently
        } finally {
          setLoading(false);
        }
      };

      fetchTemperatureData();
    }
  }, [selectedDate, location.state?.fitnessData]);

  const weeklyData = [
    { day: "Mon", temp: 36.7 },
    { day: "Tue", temp: 36.8 },
    { day: "Wed", temp: 36.6 },
    { day: "Thu", temp: 36.9 },
    { day: "Fri", temp: 36.8 },
    { day: "Sat", temp: 36.7 },
    { day: "Sun", temp: 36.8 },
  ];

  const monthlyData = [
    { week: "Week 1", temp: 36.7 },
    { week: "Week 2", temp: 36.8 },
    { week: "Week 3", temp: 36.7 },
    { week: "Week 4", temp: 36.8 },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title2 font-bold text-foreground">Temperature</h1>
            <p className="text-caption1 text-muted-foreground">Skin temperature</p>
          </div>
          <div className="w-10" />
        </div>

        <Card className="rounded-2xl border border-border bg-gradient-to-br from-orange-50 to-orange-100 p-5 shadow-lg dark:from-orange-950 dark:to-orange-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-orange-500/20">
              <Thermometer className="h-7 w-7 text-orange-600" />
            </div>
            <div className="flex-1">
              <div className="text-large-title font-bold text-foreground">
                {loading ? (
                  "Loading..."
                ) : fitnessData?.skin_temp_celsius !== null &&
                  fitnessData?.skin_temp_celsius !== undefined ? (
                  <>
                    {fitnessData.skin_temp_celsius}
                    <span className="text-title2">°C</span>
                  </>
                ) : (
                  "--"
                )}
              </div>
              <p className="text-subhead text-muted-foreground">Skin Temperature</p>
            </div>
          </div>
        </Card>
        <Card className="rounded-2xl border border-border bg-orange-50/30 p-4 shadow-lg dark:bg-orange-950/30">
          <div className="flex gap-3">
            <TrendingUp className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
            <div>
              <h4 className="mb-1 text-subhead font-semibold text-foreground">
                Sleep Tracking Required
              </h4>
              <p className="text-caption1 leading-relaxed text-muted-foreground">
                Skin temperature variability is recorded only when you wear your
                tracker watch while sleeping. Make sure to wear it overnight for
                accurate readings.
              </p>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-blue-50/30 p-4 shadow-lg dark:bg-blue-950/30">
          <div className="flex gap-3">
            <div className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-blue-500" />
            <div>
              <h4 className="mb-2 text-subhead font-semibold text-foreground">
                What is Skin Temperature Variability?
              </h4>
              <p className="mb-3 text-caption1 leading-relaxed text-muted-foreground">
                Skin temperature variability measures changes in your wrist skin
                temperature during sleep compared to your personal baseline.
                It's shown as a deviation (+ or -) from your average.
              </p>
              <p className="mb-3 text-caption1 leading-relaxed text-muted-foreground">
                Your body's temperature naturally fluctuates throughout the day
                and night. During sleep, your core temperature drops slightly,
                while skin temperature may increase as blood flow shifts to your
                extremities.
              </p>
              <p className="text-caption1 leading-relaxed text-muted-foreground">
                Significant changes from your baseline can indicate illness,
                hormonal changes (like ovulation), or environmental factors.
                Tracking these patterns can provide early warning signs of
                health changes.
              </p>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-cyan-50/30 p-4 shadow-lg dark:bg-cyan-950/30">
          <div className="flex gap-3">
            <div className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-cyan-500" />
            <div>
              <h4 className="mb-2 text-subhead font-semibold text-foreground">
                Factors That Affect Skin Temperature
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-500" />
                  <p className="text-caption1 text-muted-foreground">
                    Illness or fever developing
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-500" />
                  <p className="text-caption1 text-muted-foreground">
                    Menstrual cycle phases
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-500" />
                  <p className="text-caption1 text-muted-foreground">
                    Room temperature and bedding
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-500" />
                  <p className="text-caption1 text-muted-foreground">
                    Alcohol consumption
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-500" />
                  <p className="text-caption1 text-muted-foreground">
                    Exercise close to bedtime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
        {/* 
        <Card className="rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="m-4 grid w-full grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
            <button
              onClick={() => setActiveTab("weekly")}
              className={`rounded-lg px-3 py-2 text-subhead font-medium transition-colors ${
                activeTab === "weekly"
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setActiveTab("monthly")}
              className={`rounded-lg px-3 py-2 text-subhead font-medium transition-colors ${
                activeTab === "monthly"
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>
          </div>

          <div className="space-y-4 px-4 pb-4">
            {activeTab === "weekly" && (
              <div>
                <h3 className="mb-4 font-semibold text-gray-900">
                  This Week's Temperature
                  {dateRange.start_date && (
                    <span className="ml-2 text-subhead font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weeklyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.3}
                      />
                      <XAxis dataKey="day" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                      <YAxis
                        stroke="#6b7280"
                        tick={CHART_AXIS_TICK_LG}
                        domain={[36, 37.5]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          color: "hsl(var(--foreground))",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="temp"
                        stroke="#f97316"
                        strokeWidth={2.5}
                        dot={{ fill: "#f97316", r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {activeTab === "monthly" && (
              <div>
                <h3 className="mb-4 font-semibold text-gray-900">
                  This Month's Temperature
                  {dateRange.start_date && (
                    <span className="ml-2 text-subhead font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.3}
                      />
                      <XAxis dataKey="week" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                      <YAxis
                        stroke="#6b7280"
                        tick={CHART_AXIS_TICK_LG}
                        domain={[36, 37.5]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          color: "hsl(var(--foreground))",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="temp"
                        stroke="#f97316"
                        strokeWidth={2.5}
                        dot={{ fill: "#f97316", r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Card> */}
      </div>
    </div>
  );
};

export default TemperatureDetail;
